import { IPortal } from '@/types';
import { fromUint8Array, toUint8Array } from 'js-base64';
import {
  bytesToString,
  encodeFunctionData,
  Hex,
  parseEventLogs,
  stringToBytes,
} from 'viem';
import { getIPFSAssetSequential, jsonToFile } from '../file-utils';
import { IdentityInstance } from './identity-contract-module';
import {
  getLegacySignedMessage,
  getNewSignedMessage,
} from './key-derivation-utils';
import axios from 'axios';
import { IdentityModuleMethod, IUploadFileRequest } from './types';

import { ContentHash } from '../content-hash';
import { IDENTITY_MODULE_ABI } from '@/data/identity-module-abi';
import { KEY_STORE_UPLOAD_ROUTE } from './constants';
import { KeyStoreInstance } from './key-store';
import { AgentInstance } from './smart-account-agent';
import { KeyStoreCache } from '@/db/db';
import { addKeyStoreCache, getKeyStoreCache } from '@/db/queries';
import { publicClient } from '../contract-utils';
import { DEFAULT_TIMEOUT } from '../account-utils';
import { generateRandomNonce } from '../crypto-utils';

export const encryptAndUploadKeyStore = async (portalKeys: IPortal) => {
  const { credentials, portalAddress, serverKeys } = portalKeys;

  const jsonKeyStoreData = {
    encryptedViewSecret: await IdentityInstance.encryptEnvelope(
      toUint8Array(credentials.viewSecret)
    ),
    encryptedEditSecret: await IdentityInstance.encryptEnvelope(
      toUint8Array(credentials.editSecret)
    ),
    encryptedOwnerPrivateKey: await IdentityInstance.encryptEnvelope(
      toUint8Array(serverKeys.ownerDecryptionKey)
    ),
    encryptedMemberPrivateKey: await IdentityInstance.encryptEnvelope(
      toUint8Array(serverKeys.memberDecryptionKey)
    ),
    encryptedPortalPrivateKey: await IdentityInstance.encryptEnvelope(
      toUint8Array(serverKeys.portalDecryptionKey)
    ),
    ownerPublicKey: serverKeys.ownerEncryptionKey,
    memberPublicKey: serverKeys.memberEncryptionKey,
    portalPublicKey: serverKeys.portalEncryptionKey,
  };
  const walletClient = IdentityInstance.getWalletClient();
  let gunPassword: string | undefined;

  if (KeyStoreInstance.isLegacyUser) {
    const legacySignedMessage = await getLegacySignedMessage(walletClient);
    // @ts-ignore
    jsonKeyStoreData.gunPassword = await IdentityInstance.encryptEnvelope(
      stringToBytes(legacySignedMessage)
    );
    gunPassword = legacySignedMessage;
  } else {
    const newSignedMessage = await getNewSignedMessage(walletClient);
    // @ts-ignore
    jsonKeyStoreData.gunPassword = await IdentityInstance.encryptEnvelope(
      stringToBytes(newSignedMessage)
    );
    gunPassword = newSignedMessage;
  }

  // @ts-ignore
  jsonKeyStoreData.isLegacyUser = KeyStoreInstance.isLegacyUser;
  const fileName = fromUint8Array(generateRandomNonce());

  const keyStoreFile = jsonToFile(jsonKeyStoreData, fileName);

  const response = await uploadKeyStoreFile({
    file: keyStoreFile,
    name: fileName,
    authToken: await IdentityInstance.getAuthToken(),
    identityAddress: IdentityInstance.getIdentityAddress(),
  });
  const ipfsHash = response.data.ipfsHash;

  const keyStoreCache: KeyStoreCache = {
    portalAddress: portalAddress as Hex,
    hash: ipfsHash,
    ownerKeyPair: {
      publicKey: serverKeys.ownerEncryptionKey,
      privateKey: serverKeys.ownerDecryptionKey,
    },
    memberKeyPair: {
      publicKey: serverKeys.memberEncryptionKey,
      privateKey: serverKeys.memberDecryptionKey,
    },
    portalKeyPair: {
      publicKey: serverKeys.portalEncryptionKey,
      privateKey: serverKeys.portalDecryptionKey,
    },
    editSecret: credentials.editSecret,
    viewSecret: credentials.viewSecret,
    gunPassword: gunPassword as Hex,
    isLegacyUser: KeyStoreInstance.isLegacyUser,
  };

  return keyStoreCache;
};

export const uploadKeyStoreFile = async (request: IUploadFileRequest) => {
  try {
    const body = new FormData();
    body.append('file', request.file);
    body.append('name', request.name);

    const response = await axios.post(KEY_STORE_UPLOAD_ROUTE, body, {
      headers: {
        Authorization: `Bearer ${request.authToken}`,
        'x-api-key': 'hello_world',
        contract: request.identityAddress,
      },
    });

    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateKeyStoreData = async (
  keyStoreIPFS: string,

  portalAddress: Hex
) => {
  const callData = encodeFunctionData({
    abi: IDENTITY_MODULE_ABI,
    functionName: IdentityModuleMethod.UpdateKeyStoreContract,
    args: [ContentHash.encode('flv-ddoc', keyStoreIPFS), portalAddress],
  });

  const { logs } = await AgentInstance.executeUserOperationRequest(
    {
      data: callData,
      contractAddress: IdentityInstance.getIdentityAddress(),
    },
    DEFAULT_TIMEOUT
  );

  const parsedLogs = parseEventLogs({
    abi: IDENTITY_MODULE_ABI,
    eventName: 'UpdateKeyStoreContract',
    logs,
  });

  if (parsedLogs.length === 0)
    throw new Error('No UpdateKeyStoreContract Event Found!');

  return true;
};

export const getKeyStoreAddresses = async () => {
  const walletClient = IdentityInstance.getWalletClient();

  const response = await publicClient.readContract({
    abi: IDENTITY_MODULE_ABI,
    address: IdentityInstance.getIdentityAddress(),
    functionName: IdentityModuleMethod.GetKeyStoreAddresses,
    account: walletClient.account,
  });

  return response as Hex[];
};

export const getKeyStoreValueByAddress = async (keyStoreAddresses: Hex[]) => {
  const walletClient = IdentityInstance.getWalletClient();
  const identityModuleAddress = IdentityInstance.getIdentityAddress();

  const response = (await publicClient.readContract({
    abi: IDENTITY_MODULE_ABI,
    address: identityModuleAddress,
    functionName: IdentityModuleMethod.GetIdentityModuleKeyStoreDetails,
    args: [keyStoreAddresses],
    account: walletClient.account,
  })) as Hex[];

  for (let i = 0; i < response.length; i++) {
    const hash = response[i];
    if (ContentHash.isOfType(hash, 'flv-ddoc')) {
      return {
        index: i,
        hash: ContentHash.decode(hash),
      };
    }
  }
  return null;
};

export const getKeyStoreData = async (portalAddress: Hex, hash: string) => {
  const existingKeyStoreCache = await getKeyStoreCache(portalAddress, hash);
  if (existingKeyStoreCache) return existingKeyStoreCache;

  const ipfsData = await getIPFSAssetSequential(hash);
  if (!ipfsData) throw new Error('Failed to fetch IPFS data');
  const { data } = ipfsData;

  const keyStoreCache: KeyStoreCache = {
    portalAddress: portalAddress,
    hash: hash,
    ownerKeyPair: {
      publicKey: data.ownerPublicKey,
      privateKey: fromUint8Array(
        await IdentityInstance.decryptEnvelope(data.encryptedOwnerPrivateKey)
      ),
    },
    memberKeyPair: {
      publicKey: data.memberPublicKey,
      privateKey: fromUint8Array(
        await IdentityInstance.decryptEnvelope(data.encryptedMemberPrivateKey)
      ),
    },
    portalKeyPair: {
      publicKey: data.portalPublicKey,
      privateKey: fromUint8Array(
        await IdentityInstance.decryptEnvelope(data.encryptedPortalPrivateKey)
      ),
    },
    editSecret: fromUint8Array(
      await IdentityInstance.decryptEnvelope(data.encryptedEditSecret)
    ),
    viewSecret: fromUint8Array(
      await IdentityInstance.decryptEnvelope(data.encryptedViewSecret)
    ),

    gunPassword:
      data.gunPassword && data.isLegacyUser
        ? (bytesToString(
            await IdentityInstance.decryptEnvelope(data.gunPassword)
          ) as Hex)
        : await getNewSignedMessage(IdentityInstance.getWalletClient()),
    isLegacyUser: data.isLegacyUser || false,
  };

  await addKeyStoreCache(keyStoreCache);

  return keyStoreCache;
};
